/**
 * DashboardsList — /dashboards
 * Saved dashboards as a card grid on the ListPage grid variant. Demo pages: /file-manager (folder cards with a ⋯ menu),
 * /tables/data-tables (toolbar with search and pills). Template example: NextAdmin
 * `(dashboard)/(home)/_component/last-transactions-table` header. Server routes: list `CanViewDashboards`, create
 * `CanManageDashboards`.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, LayoutDashboard, LayoutGrid, Lock, Plus } from 'lucide-react';
import { listDashboardsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { Dashboard } from '../../generated/api/types.gen.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Callout } from '../../components/ui/Callout.js';
import { Card } from '../../components/ui/Card.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { FilterPills } from '../../components/ui/FilterPills.js';
import { SearchBar } from '../../components/ui/SearchBar.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { Toolbar } from '../../components/ui/Toolbar.js';
import { PAGE_HELP } from '../../help/index.js';

const OWNER_LABELS: Record<string, string> = {
  user: 'Personal',
  team: 'Team',
  org: 'Organization',
  system: 'System',
};

const HELP = PAGE_HELP['dashboards'];

const DashboardCard: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
  const isSystem = dashboard.ownerType === 'system';
  return (
    <Card className="flex h-full flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-muted">
            <LayoutDashboard size={18} aria-hidden="true" />
          </div>
          <Link
            to={`/dashboards/${dashboard.id}`}
            className="truncate text-sm font-semibold text-heading transition-colors hover:text-accent-text"
          >
            {dashboard.name}
          </Link>
        </div>
        {isSystem && (
          <Badge variant="neutral" icon={<Lock size={10} aria-hidden="true" />}>
            System
          </Badge>
        )}
      </div>
      {dashboard.description && <p className="line-clamp-2 text-xs text-muted">{dashboard.description}</p>}
      <div className="mt-auto flex items-center justify-between text-xs text-faint">
        <span>{OWNER_LABELS[dashboard.ownerType] ?? dashboard.ownerType}</span>
        {isSystem && <span>Read-only</span>}
      </div>
    </Card>
  );
};

const GridSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" role="status" aria-label="Loading dashboards">
    {[0, 1, 2].map((i) => (
      <Card key={i} className="space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton width={40} height={40} rounded="rounded-lg" />
          <Skeleton height={14} width="50%" />
        </div>
        <Skeleton height={10} width="90%" />
        <Skeleton height={10} width="30%" />
      </Card>
    ))}
  </div>
);

export const DashboardsList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState('all');
  const query = useQuery(listDashboardsOptions());

  const all = query.data?.data ?? [];
  const needle = search.trim().toLowerCase();
  const visible = all.filter(
    (d) =>
      (scope === 'all' || (scope === 'system' ? d.ownerType === 'system' : d.ownerType !== 'system')) &&
      (needle === '' ||
        d.name.toLowerCase().includes(needle) ||
        (d.description ?? '').toLowerCase().includes(needle) ||
        (OWNER_LABELS[d.ownerType] ?? d.ownerType).toLowerCase().includes(needle)),
  );

  const pills = [
    { id: 'all', label: 'All', count: all.length },
    { id: 'mine', label: 'Mine and shared', count: all.filter((d) => d.ownerType !== 'system').length },
    { id: 'system', label: 'System', count: all.filter((d) => d.ownerType === 'system').length },
  ];

  const newLink = (
    <Can policy="CanManageDashboards">
      <Link to="/dashboards/new" className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}>
        <Plus size={14} aria-hidden="true" />
        New dashboard
      </Link>
    </Can>
  );

  return (
    <ListPage
      title="Dashboards"
      subtitle="Saved views of your workflow outputs and metrics."
      helpContent={HELP}
      variant="grid"
      actions={newLink}
      alert={
        query.isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load dashboards"
            actions={
              <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          >
            {query.error.message}
          </Callout>
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search dashboards…" />}
          pills={<FilterPills aria-label="Dashboard owner" options={pills} value={scope} onChange={setScope} />}
        />
      }
    >
      {query.isPending && <GridSkeleton />}
      {!query.isPending && !query.isError && visible.length === 0 && (
        <Card>
          <EmptyState
            icon={LayoutGrid}
            title={all.length === 0 ? HELP.emptyState.title : 'No dashboards match'}
            description={all.length === 0 ? HELP.emptyState.description : 'Try a different search or clear the filter.'}
            learnItems={all.length === 0 ? HELP.emptyState.learnItems : undefined}
            action={all.length === 0 ? newLink : undefined}
          />
        </Card>
      )}
      {visible.length > 0 && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((d) => (
            <DashboardCard key={d.id} dashboard={d} />
          ))}
        </div>
      )}
    </ListPage>
  );
};
